import React, { useState } from "react";
import { api, extractError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Scale } from "lucide-react";
import { formatWeightKg, kgToQtl } from "../lib/weightUnits";

export default function StoreWeighQueue({ queue, onDone }) {
  const gross = queue?.gross || [];
  const tare = queue?.tare || [];
  if (!gross.length && !tare.length) return null;

  return (
    <section className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-4" data-testid="store-weigh-queue">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Store / general inward</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Packing, spares, consumables and assets — gross before store GRN, tare after inspection.
        </p>
      </div>
      {gross.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Gross pending</p>
          {gross.map((row) => (
            <StoreWeighRow key={`g-${row.gate_pass_id}`} row={row} kind="gross" onDone={onDone} />
          ))}
        </div>
      )}
      {tare.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Tare after GRN</p>
          {tare.map((row) => (
            <StoreWeighRow key={`t-${row.gate_pass_id}`} row={row} kind="tare" onDone={onDone} />
          ))}
        </div>
      )}
    </section>
  );
}

function StoreWeighRow({ row, kind, onDone }) {
  const [kg, setKg] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const qtl = kgToQtl(kg);
    if (!qtl) return toast.error("Enter a weight in kg");
    setBusy(true);
    try {
      await api.post("/store/grn/weigh", {
        gate_pass_id: row.gate_pass_id,
        kind,
        weight_qtl: qtl,
      });
      toast.success(kind === "gross" ? "Gross recorded — send to store" : "Tare recorded");
      setKg("");
      onDone?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="rounded-xl border border-border bg-card px-3 py-3 flex flex-col sm:flex-row sm:items-end gap-3">
      <div className="min-w-0 flex-1">
        <div className="font-medium text-foreground">{row.truck_no} · {row.gate_pass_no}</div>
        <p className="text-xs text-muted-foreground truncate">
          {row.supplier_name || row.party_name || "—"} · {row.po_number || "No PO"} · {row.procurement_type_label}
          {kind === "tare" && row.gross_weight_qtl != null ? ` · Gross ${formatWeightKg(row.gross_weight_qtl)}` : ""}
        </p>
      </div>
      <div className="flex items-end gap-2 w-full sm:w-auto">
        <div className="flex-1 sm:w-32">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Weight (kg)</Label>
          <Input
            type="number"
            inputMode="decimal"
            className="min-h-11 mt-1"
            value={kg}
            onChange={(e) => setKg(e.target.value)}
          />
        </div>
        <Button type="button" className="min-h-11 bg-bansal-sage hover:bg-bansal-sageDark text-white" onClick={save} disabled={busy}>
          <Scale className="h-4 w-4 mr-1" /> {kind === "gross" ? "Gross" : "Tare"}
        </Button>
      </div>
    </div>
  );
}
